import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { DocsMarkdown } from "../DocsMarkdown";
import {
  audienceLabelFor,
  getArticle,
  siblingArticles,
} from "../docsContent";

export function DocsArticlePage() {
  const { slug } = useParams({ from: "/docs/$slug" });
  const article = getArticle(slug);

  if (!article) {
    return (
      <div data-testid="docs-article-missing" className="mx-auto max-w-2xl">
        <h2 className="font-display text-xl font-semibold text-ink">Article not found</h2>
        <p className="mt-2 text-ink-muted">
          The help article “{slug}” doesn’t exist or was moved.
        </p>
        <Link
          to="/docs"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to all help
        </Link>
      </div>
    );
  }

  const { prev, next } = siblingArticles(slug);
  const toc = article.headings.filter((h) => h.depth === 2);

  return (
    <div data-testid="docs-article" className="lg:flex lg:gap-8">
      <article className="min-w-0 flex-1">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-1 text-xs text-ink-muted">
          <Link to="/docs" className="hover:text-ink">
            Help
          </Link>
          <ChevronRight className="size-3" />
          <span>{audienceLabelFor(article.audience)}</span>
        </nav>

        <DocsMarkdown body={article.body} />

        {/* Prev / next within the audience */}
        {(prev || next) && (
          <nav
            aria-label="More in this section"
            data-testid="docs-prevnext"
            className="mt-10 grid gap-3 border-t border-rule pt-6 sm:grid-cols-2"
          >
            {prev ? (
              <Link
                to="/docs/$slug"
                params={{ slug: prev.slug }}
                className="group flex flex-col rounded border border-rule bg-surface px-3 py-2.5 shadow-sm transition-colors hover:border-accent hover:bg-field"
              >
                <span className="flex items-center gap-1 text-xs text-ink-muted">
                  <ArrowLeft className="size-3.5" /> Previous
                </span>
                <span className="mt-0.5 text-sm font-medium text-ink group-hover:text-accent">
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to="/docs/$slug"
                params={{ slug: next.slug }}
                className="group flex flex-col rounded border border-rule bg-surface px-3 py-2.5 text-right shadow-sm transition-colors hover:border-accent hover:bg-field"
              >
                <span className="flex items-center justify-end gap-1 text-xs text-ink-muted">
                  Next <ArrowRight className="size-3.5" />
                </span>
                <span className="mt-0.5 text-sm font-medium text-ink group-hover:text-accent">
                  {next.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
          </nav>
        )}
      </article>

      {/* On-page table of contents */}
      {toc.length > 1 && (
        <aside
          aria-label="On this page"
          data-testid="docs-toc"
          className="mt-8 hidden w-52 shrink-0 lg:mt-0 lg:block"
        >
          <div className="sticky top-20">
            <p className="mb-2 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
              On this page
            </p>
            <ul className="space-y-1.5 border-l border-rule">
              {toc.map((h) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    className="-ml-px block border-l border-transparent pl-3 text-sm text-ink-muted transition-colors hover:border-accent hover:text-ink"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}
    </div>
  );
}
